import {
  Component,
  AfterViewInit,
  ElementRef,
  Input,
  ViewChild,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import * as echarts from 'echarts';
import { BehaviouralAssessmentGraphData } from '../assesment-fba.interface';
import { BehaviourFunction } from '../../../../insights.enums';
import { CallbackDataParams } from 'echarts/types/dist/shared';

@Component({
  selector: 'app-screener-results-graph',
  standalone: true,
  imports: [],
  templateUrl: './screener-results-graph.component.html',
  styleUrl: './screener-results-graph.component.scss',
})
export class ScreenerResultsGraphComponent implements AfterViewInit, OnChanges {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @Output() chartImage = new EventEmitter<string>();
  chartInstance!: echarts.ECharts;
  @Input() graphData: BehaviouralAssessmentGraphData[] = [];
  @Input() chartId = '';
  data: BehaviouralAssessmentGraphData[] = this
    .graphData as BehaviouralAssessmentGraphData[];
  sortedData: { name: string; value: number }[] = [];

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphData'] && this.chartInstance) {
      this.initChart();
    }
  }

  ngAfterViewInit(): void {
    const chartDom = document.getElementById(this.chartId)!;
    if (chartDom) {
      chartDom.innerHTML = '';
    }
    this.initChart();
  }

  private prepareChartData(): { name: string; value: number }[] {
    const data = [
      {
        name: BehaviourFunction.SENSORY,
        value: this.getScoreByFunctionName(BehaviourFunction.SENSORY) || 0,
      },
      {
        name: BehaviourFunction.ESCAPE_AVOIDANCE,
        value:
          this.getScoreByFunctionName(BehaviourFunction.ESCAPE_AVOIDANCE) || 0,
      },
      {
        name: BehaviourFunction.TANGIBLE,
        value: this.getScoreByFunctionName(BehaviourFunction.TANGIBLE) || 0,
      },
      {
        name: BehaviourFunction.GAINING_ATTENTION,
        value:
          this.getScoreByFunctionName(BehaviourFunction.GAINING_ATTENTION) || 0,
      },
    ];

    return data.sort((a, b) => b.value - a.value);
  }

  private initChart(): void {
    const chartDom = document.getElementById(this.chartId)!;
    const myChart = echarts.init(chartDom);
    this.chartInstance = myChart;

    this.sortedData = this.prepareChartData();
    const categoryNames = this.sortedData.map(item => item.name);

    const option = {
      title: {
        text: 'Screener Results',
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#101828',
        },
      },
      grid: {
        left: '10%', // Increased left margin to make room for Y-axis labels
        right: '5%',
        top: 80,
        bottom: '10%',
        containLabel: true,
      },
      legend: {
        top: 40,
        left: 'center',
        orient: 'horizontal',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontSize: 12,
          fontWeight: '400',
          color: '#475467',
        },
        data: categoryNames,
      },
      xAxis: {
        type: 'category',
        data: categoryNames,
        axisLabel: {
          interval: 0,
          rotate: 30,
          fontSize: 12,
          color: '#475467',
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#EAECF0',
          },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Score',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: '500',
          color: '#475467',
          padding: [0, 0, 10, 0],
        },
        min: 0,
        max: 12,
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#EAECF0',
          },
        },
        axisLabel: {
          show: true, // Ensure labels are shown
          formatter: '{value}',
          color: '#475467',
          fontSize: 12,
          margin: 10, // Add margin to prevent labels from being cut off
        },
        axisLine: {
          show: true, // Show axis line for better visibility
          lineStyle: {
            color: '#EAECF0',
          },
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (
          params: echarts.TooltipComponentFormatterCallbackParams
        ) => {
          const dataIndex = Array.isArray(params) ? params[0].dataIndex : 0;
          const name = categoryNames[dataIndex];
          const value = this.sortedData[dataIndex].value;
          return `${name}: ${value}`;
        },
      },
      series: [
        {
          name: 'Score',
          type: 'bar',
          barWidth: '40%',
          data: this.sortedData.map(item => item.value),
          itemStyle: {
            barBorderRadius: [6, 6, 0, 0],
            color: (params: CallbackDataParams) => {
              const colorMap: Record<string, string> = {
                [BehaviourFunction.SENSORY]: '#4E97F3',
                [BehaviourFunction.ESCAPE_AVOIDANCE]: '#F7936F',
                [BehaviourFunction.TANGIBLE]: '#66C194',
                [BehaviourFunction.GAINING_ATTENTION]: '#9B8AFB',
              };
              return colorMap[categoryNames[params.dataIndex]] || '#4E97F3';
            },
          },
          label: {
            show: false,
          },
        },
      ],
    };

    myChart.setOption(option);

    // Handle window resize to make chart responsive
    window.addEventListener('resize', () => {
      myChart.resize();
    });

    myChart.on('finished', () => {
      this.emitChartAsImage(); // Call your function after full rendering
    });
  }

  emitChartAsImage() {
    const chartImageBase64 = this.chartInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });
    this.chartImage.emit(chartImageBase64);
  }

  getScoreByFunctionName(name: string): number | undefined {
    const result = this.graphData.find(
      item => item.behaviourFunctionName === name
    );
    return result ? result.score : undefined;
  }
}
